import { injectable, inject } from 'inversify';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ILocalisationService } from '@/app-modules/localisation-service';
import { ISweetAlertService } from '@/app-modules/sweet-alert-service';
import { Lang } from 'vuejs-localization';
import { IHttpRequest, IHttpService } from '@/app-modules/http';
import { base64 } from '@/app-modules/utils';
import { container } from '@/app-modules/_ioc';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { User } from '@/app-modules/store/modules/user';
import { RoomUserModel, IClientData } from '@/components/chat/_domain';
import { ITogglesService } from '@/app-modules/config';
import moment from 'moment';

interface ITechnicalIssueReferral {
  username: string;
  description: string;
  cmUsername: string;
  dateTime: string;
  gameName: string;
  platformVersion: string;
  readableClientType: IClientData;
}

interface ITechnicalIssueService {
  referUser(user: RoomUserModel, actionReason?: string): Promise<void>;
}

@injectable()
class TechnicalIssueService implements ITechnicalIssueService {
  private readonly $lang: Lang;

  constructor(
    @inject(SERVICE_IDENTIFIERS.ILocalisationService) private readonly localisationService: ILocalisationService,
    @inject(SERVICE_IDENTIFIERS.ISweetAlertService) private readonly sweetAlertService: ISweetAlertService,
    @inject(SERVICE_IDENTIFIERS.IHttpService) private readonly httpService: IHttpService,
    @inject(SERVICE_IDENTIFIERS.ITogglesService) private readonly toggleService: ITogglesService
  ) {
    this.$lang = this.localisationService.getLangInstance();
  }

  public async referUser(user: RoomUserModel, actionReason?: string): Promise<void> {
    const title: string = this.$lang.chat.technicalIssueReferral.title;
    const successMessage = this.formatMessageForUser(this.$lang.chat.technicalIssueReferral.success, user.username);
    const failureMessage = this.formatMessageForUser(this.$lang.chat.technicalIssueReferral.failed, user.username);
    const inputMessage = this.formatMessageForUser(this.$lang.chat.technicalIssueReferral.input, user.username);
    const description = actionReason ? actionReason : await this.showTechnicalIssueReferralEntryMessageForUser(inputMessage, title, user.username);

    if (description !== undefined && description !== '') {
      const technicalIssueReferralSuccess = await this.sendTechnicalIssueReferralToMailGroup(user, description);
      this.showSuccessOrFailureMessageForUser(technicalIssueReferralSuccess, successMessage, failureMessage, title);
    }
  }

  private async sendTechnicalIssueReferralToMailGroup(user: RoomUserModel, description: string): Promise<boolean> {
    const persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
    const cmUser = persistedStore.Get(User);
    const request =  this.buildTechnicalIssueReferralRequest(user, description, cmUser.username);

    const httpRequest = this.getHttpRequestForTechincalIssueReferralRequest(request, cmUser);

    try {
      const useNewEndPoint = await this.toggleService.isLive('CIC_330_UseNETTechnicalIssueEndPoint');
      const response = await this.httpService.post(httpRequest, useNewEndPoint);
      if (useNewEndPoint) {
        return (response.status === 200) ? true : false;
      }
      else {
        return (response.status === 200 && response.data.Status === 200) ? true : false;
      }

    } catch {
      return false;
    }
  }

  private async showTechnicalIssueReferralEntryMessageForUser(messagePopupText: string, messagePopupTitleText: string, username: string): Promise<string> {
    const refocus = true;
    const {value: result} = await this.sweetAlertService.show({
      title: messagePopupTitleText,
      text: messagePopupText,
      input: 'textarea',
      inputPlaceholder: 'Enter message',
      allowOutsideClick: false,
      focusConfirm: false,
      showConfirmButton: true,
      showCancelButton: true
    }, refocus);
    return result;
  }

  private showSuccessOrFailureMessageForUser(success: boolean, successMessage: string, failureMessage: string, messagePopupTitleText: string) {
    const refocus = true;
    this.sweetAlertService.show({
      title: messagePopupTitleText,
      text: success ? successMessage : failureMessage,
      type: success ? 'success' : 'error'
    }, refocus);
  }

  private formatMessageForUser(message: string, username: string): string {
    return message.format({ username });
  }

  private buildTechnicalIssueReferralRequest(user: RoomUserModel, description: string, cmUsername: string): ITechnicalIssueReferral {
    return {
      username: user.username,
      platformVersion: user.getClientVersion(),
      readableClientType: user.readableClientType,
      gameName: user.gameName,
      dateTime: moment().format('DD-MM-YYYY HH:mm:ss'),
      description,
      cmUsername
    };
  }

  private getHttpRequestForTechincalIssueReferralRequest(request: ITechnicalIssueReferral, cmUser: User): IHttpRequest {
    const encodedAuth = base64.Encode(`${cmUser.userId}:${cmUser.crosscheckId}`);
    const httpRequest: IHttpRequest = {
      Url: '/technicalissue/referral',
      XChatModsAuthorization: encodedAuth,
      Payload: request
    };
    return httpRequest;
  }
}

export { ITechnicalIssueService, TechnicalIssueService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/technical-issue-service/technical-issue-service.ts